<script lang="ts">
const __DsBlock = {
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script setup lang="ts">
import type { CompositePrimaryKey } from '#layers/data-model';
import type { RelatedDisplayItem } from '~/components/RelatedItemListCard.vue';
import type { RelationFormControlProps } from '~/composables/form-block';
import type { FormSaveContext, FormSaveError } from '~/composables/form-block/types';
import type { InputBlockProps } from '~/types/input-block';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay, useStore } from '#imports';
import { canAssignPolicy, getKeyId } from '#layers/access';
import { toStructuredError } from '#layers/base/utils/error';
import { defineBlock } from '#layers/block-engine';
import { useCollectionByName } from '#layers/data-model';
import RelatedItemListCard from '~/components/RelatedItemListCard.vue';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { useRelationFormField } from '~/composables/form-block/use-relation-form-field';
import { connectRolePolicies, disconnectRolePolicies } from '~/utils/api/role-policies';
import { buildAssignmentFilter } from './assignment-filter';
import { useAssignmentEntitlement } from './use-assignment-entitlement';
import { usePendingConnections } from './use-pending-connections';

interface MonospacePolicy {
	id: string;
	name: string;
}

export interface InputPolicyAssignmentBlockProps extends InputBlockProps, RelationFormControlProps {}

const props = defineProps<InputPolicyAssignmentBlockProps>();

const LazyCollectionListingDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionListingDrawer.vue'));

;

const store = useStore();
const { t } = useI18n();

const formBlockContext = injectFormBlockContext();
const { collection: policyCollection } = useCollectionByName('MonospacePolicy');
const { referencedItemKey: roleKey } = useRelationFormField(props);

const { allowedAssignableIds: allowedPolicyIds, canAssign, canDisconnectItem: canDisconnectPolicy, allowedDisconnections } = useAssignmentEntitlement(canAssignPolicy());

const { data: rolePolicies, refresh: refreshRolePolicies } = store.RawMonospacePolicy.query((q) => q.many({
	filter: {
		roles: { _some: { roleId: roleKey.value!.id } },
	},
	// TODO(hannes) no-cache required here since we can't resolve relational filters locally
	fetchPolicy: 'no-cache',
}));

const currentPolicyIds = computed(() => rolePolicies.value.map(({ id }) => id));

const {
	pendingConnections,
	pendingDisconnections,
	effectiveIds,
	hasChanges,
	connect,
	disconnect,
	undo,
	getItemState,
	clearConnections,
	clearDisconnections,
} = usePendingConnections(currentPolicyIds);

// Load pending policies into the cache so the effective-id query can stay cache-only.
store.MonospacePolicy.query((q) => q.many({ filter: { id: { _in: [...pendingConnections.value] } } }));
const { data: policyItems } = store.MonospacePolicy.query((q) => q.many({ filter: { id: { _in: [...effectiveIds.value] } }, fetchPolicy: 'cache-only' }));

const displayItems = computed<RelatedDisplayItem<MonospacePolicy>[]>(() =>
	policyItems.value.map((item) => ({
		id: item.id,
		data: item,
		state: getItemState(item.id),
	})),
);

async function handleSelectPolicies() {
	if (!policyCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: policyCollection.value.id,
			selectionMode: 'multiple',
			filter: buildAssignmentFilter(allowedPolicyIds.value, [...effectiveIds.value]),
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result?.selection) {
		for (const key of result.selection) {
			connect(getKeyId(key)!);
		}
	}
}

function handleRemovePolicy(item: MonospacePolicy) {
	disconnect(item.id);
}

function handleUndoPolicy(item: MonospacePolicy) {
	undo(item.id);
}

formBlockContext.onSave(async ({ itemKey }: FormSaveContext): Promise<FormSaveError[]> => {
	const errors: FormSaveError[] = [];
	const roleId = getKeyId({ id: itemKey.id })!;

	if (pendingConnections.value.size > 0) {
		try {
			await connectRolePolicies(roleId, [...pendingConnections.value]);
			clearConnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.attach_policies_failed'), source: toStructuredError(error) });
		}
	}

	// Re-check the allowlist at save time so a staged removal can never detach a policy the user may not assign.
	const policyIdsToDisconnect = allowedDisconnections([...pendingDisconnections.value]);

	if (policyIdsToDisconnect.length > 0) {
		try {
			const junctionItems = store.MonospaceRolePolicy.peekMany({
				filter: { roleId, policyId: { _in: policyIdsToDisconnect } },
			});

			await disconnectRolePolicies(roleId, policyIdsToDisconnect);

			for (const item of junctionItems) {
				store.MonospaceRolePolicy.clearItem(store.MonospaceRolePolicy.getKey(item)!);
			}

			clearDisconnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.detach_policies_failed'), source: toStructuredError(error) });
		}
	}

	try {
		await refreshRolePolicies();
	}
	catch {
		// Refresh failure is non-fatal - user can manually refresh
	}

	return errors;
});

formBlockContext.reportHasChanges(() => hasChanges.value);
</script>

<template>
	<RelatedItemListCard
		v-if="policyCollection"
		:items="displayItems"
		:collection-id="policyCollection.id"
		:disabled
		:disabled-notice
		:show-edit="false"
		:show-create="false"
		:permissions="{ canConnect: canAssign, canDisconnectItem: canDisconnectPolicy }"
		show-add-existing
		@select="handleSelectPolicies"
		@remove="handleRemovePolicy"
		@undo="handleUndoPolicy"
	/>
</template>
